export class ImageConverter {
  constructor(options = {}) {
    // Default options
    this.options = {
      width: 300,
      height: 300,
      format: 'jpeg',
      quality: 0.9,
      fitMethod: 'crop', // 'crop', 'contain', or 'stretch'
      backgroundColor: '#FFFFFF', // For 'contain' method
      outputTarget: null,
      onProcessed: null,
      ...options,
    }
  }

  /**
   * Process an image file with current options
   * @param {File} file - Image file from input or drop event
   * @returns {Promise} Promise that resolves to the processed image data URL
   */
  processImageFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        const img = new Image()

        img.onload = () => {
          const result = this.processImage(img)
          resolve(result)
        }

        img.onerror = () => {
          reject(new Error('Failed to load image'))
        }

        img.src = e.target.result
      }

      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }

      reader.readAsDataURL(file)
    })
  }

  /**
   * Process an image element with current options
   * @param {HTMLImageElement} img - Image element
   * @returns {String} Processed image data URL
   */
  processImage(img) {
    // Create canvas for processing
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    // Set canvas dimensions
    canvas.width = this.options.width
    canvas.height = this.options.height

    // Fill with background color if needed
    if (this.options.fitMethod === 'contain') {
      ctx.fillStyle = this.options.backgroundColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    // Calculate dimensions based on fit method
    const dimensions = this.calculateDimensions(img)

    // Draw image on canvas
    ctx.drawImage(
      img,
      dimensions.sourceX,
      dimensions.sourceY,
      dimensions.sourceWidth,
      dimensions.sourceHeight,
      dimensions.destX,
      dimensions.destY,
      dimensions.destWidth,
      dimensions.destHeight,
    )

    // Convert to specified format
    const mimeType = `image/${this.options.format}`
    const dataUrl = canvas.toDataURL(mimeType, this.options.quality)

    // Process the result
    this.handleResult(dataUrl)

    return dataUrl
  }

  /**
   * Calculate dimensions for drawing the image based on fit method
   * @param {HTMLImageElement} img - Image element
   * @returns {Object} Dimensions for source and destination
   */
  calculateDimensions(img) {
    const { width, height, fitMethod } = this.options
    const imgRatio = img.width / img.height
    const targetRatio = width / height

    // Default values
    const dimensions = {
      sourceX: 0,
      sourceY: 0,
      sourceWidth: img.width,
      sourceHeight: img.height,
      destX: 0,
      destY: 0,
      destWidth: width,
      destHeight: height,
    }

    if (fitMethod === 'crop') {
      // Center crop to fill the target dimensions
      if (imgRatio > targetRatio) {
        // Landscape image
        dimensions.sourceWidth = img.height * targetRatio
        dimensions.sourceX = (img.width - dimensions.sourceWidth) / 2
      }
      else if (imgRatio < targetRatio) {
        // Portrait image
        dimensions.sourceHeight = img.width / targetRatio
        dimensions.sourceY = (img.height - dimensions.sourceHeight) / 2
      }
    }
    else if (fitMethod === 'contain') {
      // Scale to fit inside target dimensions while preserving aspect ratio
      if (imgRatio > targetRatio) {
        // Landscape image
        dimensions.destWidth = width
        dimensions.destHeight = width / imgRatio
        dimensions.destY = (height - dimensions.destHeight) / 2
      }
      else {
        // Portrait image
        dimensions.destHeight = height
        dimensions.destWidth = height * imgRatio
        dimensions.destX = (width - dimensions.destWidth) / 2
      }
    }
    // 'stretch' will use default values to stretch the image

    return dimensions
  }

  /**
   * Handle the processed image result
   * @param {String} dataUrl - Processed image data URL
   */
  handleResult(dataUrl) {
    // Extract base64 data without the prefix
    const base64Data = dataUrl.split(',')[1]

    // Set value in target element if provided
    if (this.options.outputTarget) {
      if (typeof this.options.outputTarget === 'string') {
        const element = document.querySelector(this.options.outputTarget)
        if (element) {
          element.value = base64Data
        }
        else {
          console.error(`Output target element not found: ${this.options.outputTarget}`)
        }
      }
      else if (this.options.outputTarget instanceof HTMLElement) {
        this.options.outputTarget.value = base64Data
      }
    }

    // Call callback function if provided
    if (typeof this.options.onProcessed === 'function') {
      this.options.onProcessed(dataUrl, base64Data)
    }
  }

  /**
   * Update processor options
   * @param {Object} newOptions - New options to set
   */
  updateOptions(newOptions = {}) {
    this.options = {
      ...this.options,
      ...newOptions,
    }
    return this
  }

  /**
   * Create an image preview element
   * @param {String} dataUrl - Image data URL
   * @param {String|HTMLElement} container - Container selector or element
   * @returns {HTMLImageElement} The created image element
   */
  createPreview(dataUrl, container) {
    const img = new Image()
    img.src = dataUrl
    img.className = 'image-preview'

    const targetContainer = typeof container === 'string'
      ? document.querySelector(container)
      : container

    if (targetContainer) {
      targetContainer.innerHTML = ''
      targetContainer.appendChild(img)
    }

    return img
  }
}
